import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faSpinner,
  faBan,
  faPlay,
  faChartBar,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import FilterPanel from "../common/FilterPanel";
import FilterChips, { countActive } from "../common/FilterChips";
import { BOOST_FILTERS, buildFlatPayload } from "../../config/filterConfigs";

const STATUS_COLORS = {
  pending_payment: "bg-yellow-100 text-yellow-700",
  processing:      "bg-blue-100 text-blue-700",
  active:          "bg-green-100 text-green-700",
  expired:         "bg-gray-100 text-gray-500",
  cancelled:       "bg-red-100 text-red-500",
};

const fmt = (n) => (n || 0).toLocaleString("en-IN");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const SummaryCard = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color || "text-gray-800"}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const BoostConsole = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [boosts, setBoosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [actionId, setActionId] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await func.get("mst-admin/boosts/analytics");
      if (res?.status === 200 && res.data?.success) {
        setAnalytics(res.data.analytics);
      }
    } catch {}
  }, []);

  const fetchBoosts = useCallback(async (p = 1, f = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      Object.entries(buildFlatPayload(f)).forEach(([k, v]) => params.append(k, v));
      const res = await func.get(`mst-admin/boosts?${params}`);
      if (res?.status === 200 && res.data?.success) {
        setBoosts(res.data.boosts || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error("Failed to load boosts");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchBoosts(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reload]);

  const applyFilters = () => { setPage(1); setReload((r) => !r); };
  const resetFilters = () => { setFilters({}); setPage(1); setReload((r) => !r); };
  const clearFilterKeys = (keys) => {
    const next = { ...filters };
    keys.forEach((k) => { next[k] = ""; });
    setFilters(next);
    setPage(1);
    setReload((r) => !r);
  };

  const handleAction = async (id, action) => {
    if (!window.confirm(`${action === "activate" ? "Activate" : "Cancel"} this boost?`)) return;
    setActionId(id);
    try {
      const res = await func.post(`mst-admin/boosts/${id}/${action}`, {});
      if (res?.status === 200 && res.data?.success) {
        toast.success(res.data.message);
        fetchBoosts(page, filters);
        fetchAnalytics();
      } else {
        toast.error(res?.data?.message || "Action failed");
      }
    } catch {
      toast.error("Action failed");
    }
    setActionId(null);
  };

  const statusBreakdown = analytics?.statusBreakdown ?? {};
  const scopeBreakdown = analytics?.scopeBreakdown ?? [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <FontAwesomeIcon icon={faRocket} className="text-2xl text-purple-600" />
        <h1 className="text-2xl font-bold text-gray-800">Boost Console</h1>
      </div>

      {/* Analytics summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Total Revenue" value={`₹${fmt(analytics?.totalRevenue ?? 0)}`} color="text-green-600" />
        <SummaryCard label="Paid Boosts" value={fmt(analytics?.totalPaidBoosts ?? 0)} />
        <SummaryCard label="Active Now" value={fmt(statusBreakdown.active ?? 0)} color="text-blue-600" />
        <SummaryCard label="Pending Payment" value={fmt(statusBreakdown.pending_payment ?? 0)} color="text-yellow-600" />
      </div>

      {/* Scope breakdown */}
      {scopeBreakdown.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
            <FontAwesomeIcon icon={faChartBar} /> Scope Breakdown
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b text-left">
                  <th className="pb-2 pr-4">Scope</th>
                  <th className="pb-2 pr-4">Count</th>
                  <th className="pb-2 pr-4">Revenue</th>
                  <th className="pb-2 pr-4">Avg Impressions</th>
                  <th className="pb-2">Avg Visits</th>
                </tr>
              </thead>
              <tbody>
                {scopeBreakdown.map((s) => (
                  <tr key={s.scopeType} className="border-b border-gray-50">
                    <td className="py-2 pr-4 font-medium">{s.scopeType}</td>
                    <td className="py-2 pr-4">{fmt(s.count)}</td>
                    <td className="py-2 pr-4 text-green-600">₹{fmt(s.revenue)}</td>
                    <td className="py-2 pr-4">{fmt(s.avgImpressions)}</td>
                    <td className="py-2">{fmt(s.avgVisits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Boost list */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="p-4 border-b flex items-center gap-3">
          <p className="font-semibold text-gray-700">All Boosts ({fmt(total)})</p>
          <button
            onClick={() => setShowFilter(true)}
            className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            <FontAwesomeIcon icon={faSlidersH} />
            Filters
            {countActive(BOOST_FILTERS, filters) > 0 && (
              <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-purple-600 text-white rounded-full w-5 h-5">
                {countActive(BOOST_FILTERS, filters)}
              </span>
            )}
          </button>
        </div>
        <div className="px-4 pt-3">
          <FilterChips config={BOOST_FILTERS} filters={filters} onClearKeys={clearFilterKeys} onClearAll={resetFilters} />
        </div>
        <FilterPanel
          isOpen={showFilter}
          onClose={() => setShowFilter(false)}
          title="Filter Boosts"
          config={BOOST_FILTERS}
          filters={filters}
          onChange={setFilters}
          onApply={applyFilters}
          onReset={resetFilters}
        />

        {loading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b text-left">
                  <th className="px-4 py-3">User ID</th>
                  <th className="px-4 py-3">Scope</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Impressions</th>
                  <th className="px-4 py-3">Visits</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {boosts.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-gray-400">No boosts found.</td>
                  </tr>
                ) : boosts.map((b) => (
                  <tr key={b._id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{b.userId?.toString().slice(-6)}</td>
                    <td className="px-4 py-3">{b.scopeType}</td>
                    <td className="px-4 py-3">{b.durationDays}d</td>
                    <td className="px-4 py-3 font-medium">{b.paidWith === 'credits' ? `${fmt(b.amount)} cr` : `₹${fmt(b.amount)}`}</td>
                    <td className="px-4 py-3">{fmt(b.impressions)}</td>
                    <td className="px-4 py-3">{fmt(b.profileVisits)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || ""}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{fmtDate(b.expiresAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {b.status === "processing" && (
                          <button
                            onClick={() => handleAction(b._id, "activate")}
                            disabled={actionId === b._id}
                            className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"
                          >
                            <FontAwesomeIcon icon={actionId === b._id ? faSpinner : faPlay} spin={actionId === b._id} />
                            Activate
                          </button>
                        )}
                        {["pending_payment", "processing", "active"].includes(b.status) && (
                          <button
                            onClick={() => handleAction(b._id, "cancel")}
                            disabled={actionId === b._id}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                          >
                            <FontAwesomeIcon icon={faBan} />
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-between items-center p-4 border-t text-sm text-gray-500">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Prev
            </button>
            <span>Page {page} of {Math.ceil(total / 20)}</span>
            <button
              disabled={page >= Math.ceil(total / 20)}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoostConsole;
